import { useState, useCallback, useMemo, useEffect } from "react";
import { pickQuestions, getAllQuestions } from "../data/index";
import { savePracticeSession, clearPracticeSession } from "../utils/practiceSession";
import { recordQuestionAttempt } from "../utils/analytics";
import QuestionCard from "./QuestionCard";
import Timer from "./Timer";
import ProgressBar from "./ProgressBar";

const PASS_PCT = 80;

export default function QuizScreen({ section, mode, examCount, timeLimitMinutes, customQuestions, initialAnswers, initialFlagged, initialIdx, onFinish, onHome }) {
  const isExam = mode === "exam";

  const questions = useMemo(
    () => customQuestions ?? (mode === "exam" ? pickQuestions(section, examCount) : getAllQuestions(section)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [currentIdx, setCurrentIdx] = useState(initialIdx ?? 0);
  const [answers,    setAnswers]    = useState(initialAnswers ?? {});
  const [flagged,    setFlagged]    = useState(initialFlagged ?? {});
  const [skipped,    setSkipped]    = useState({});           // explicitly skipped (exam only)
  const [showAnswer, setShowAnswer] = useState((initialAnswers ?? {})[initialIdx ?? 0] !== undefined);
  const [paused,     setPaused]     = useState(false);
  const [showMap,    setShowMap]    = useState(false);

  useEffect(() => {
    if (isExam || Object.keys(answers).length === 0) return;
    savePracticeSession(section, { questions, answers, flagged, currentIdx });
  }, [isExam, section, questions, answers, flagged, currentIdx]);

  const handleSelect = (optIdx) => {
    // Answering a previously-skipped question removes it from skipped
    if (skipped[currentIdx]) setSkipped(prev => { const n = { ...prev }; delete n[currentIdx]; return n; });
    setAnswers(prev => ({ ...prev, [currentIdx]: optIdx }));
    setShowAnswer(true);
  };

  const handleSkip = () => {
    // Mark explicitly skipped and advance
    setSkipped(prev => ({ ...prev, [currentIdx]: true }));
    if (currentIdx < questions.length - 1) goTo(currentIdx + 1);
  };

  const handleTimeUp = useCallback(() => {
    // Record all answered questions
    Object.keys(answers).forEach(idx => {
      const qIdx = parseInt(idx, 10);
      if (questions[qIdx]) {
        const isCorrect = answers[qIdx] === questions[qIdx].answer;
        recordQuestionAttempt(questions[qIdx].id, isCorrect, 0, section);
      }
    });
    if (!isExam) clearPracticeSession(section);
    onFinish(answers, questions);
  }, [answers, questions, onFinish, isExam, section]);

  const goTo = (idx) => {
    // Record the current question before moving
    if (answers[currentIdx] !== undefined && questions[currentIdx]) {
      const isCorrect = answers[currentIdx] === questions[currentIdx].answer;
      recordQuestionAttempt(questions[currentIdx].id, isCorrect, 0, section);
    }
    setCurrentIdx(idx);
    setShowAnswer(answers[idx] !== undefined);
    setShowMap(false);
  };

  const goNext = () => { if (currentIdx < questions.length - 1) goTo(currentIdx + 1); };
  const goPrev = () => { if (currentIdx > 0) goTo(currentIdx - 1); };

  const handleSubmit = () => {
    const skippedCount  = Object.values(skipped).filter(Boolean).length;
    const unansweredCount = questions.length - Object.keys(answers).length - skippedCount;
    const parts = [];
    if (skippedCount  > 0) parts.push(`${skippedCount} skipped`);
    if (unansweredCount > 0) parts.push(`${unansweredCount} not reached`);
    const msg = parts.length > 0
      ? `You have ${parts.join(" and ")} — these count as wrong. Submit anyway?`
      : "Submit the test? You won't be able to change your answers.";
    if (window.confirm(msg)) {
      // Record all answered questions before finishing
      Object.keys(answers).forEach(idx => {
        const qIdx = parseInt(idx, 10);
        if (questions[qIdx]) {
          const isCorrect = answers[qIdx] === questions[qIdx].answer;
          recordQuestionAttempt(questions[qIdx].id, isCorrect, 0, section);
        }
      });
      if (!isExam) clearPracticeSession(section);
      onFinish(answers, questions);
    }
  };

  // Live score — tracks correct / wrong / explicitly-skipped / not-yet-reached
  const examLive = useMemo(() => {
    if (!isExam) return null;
    let correct = 0, wrong = 0, explicitSkips = 0;
    questions.forEach((q, i) => {
      if (answers[i] !== undefined) {
        if (answers[i] === q.answer) correct++;
        else wrong++;
      } else if (skipped[i]) {
        explicitSkips++;
      }
    });
    const remaining = questions.length - correct - wrong - explicitSkips; // not yet reached
    const needed    = Math.ceil(questions.length * PASS_PCT / 100);
    const toGo      = Math.max(0, needed - correct);
    const pct       = Math.round((correct / questions.length) * 100);
    // Can still pass if answering all remaining + going back to skipped questions
    const canPass   = (questions.length - wrong) >= needed;
    return { correct, wrong, skipped: explicitSkips, remaining, pct, needed, toGo, canPass };
  }, [isExam, answers, skipped, questions]);

  const answered        = Object.keys(answers).length;
  const flaggedCount    = Object.values(flagged).filter(Boolean).length;
  const currentAnswered = answers[currentIdx] !== undefined;
  const currentSkipped  = !!skipped[currentIdx];
  const isLastQ         = currentIdx === questions.length - 1;

  // Nav button state
  const showSkipBtn = isExam && !currentAnswered && !isLastQ;

  return (
    <div className="quiz-screen">
      <div className="quiz-topbar">
        <button className="btn btn-sm btn-outline" onClick={onHome}>✕ Exit</button>
        <div className="quiz-top-center">
          <span className="quiz-section-title">{section}</span>
          <span className={`mode-pill ${isExam ? "exam-pill" : "practice-pill"}`}>
            {isExam ? "🎯 Exam" : "📚 Practice"}
          </span>
        </div>
        <Timer duration={timeLimitMinutes * 60} onTimeUp={handleTimeUp} paused={paused} />
      </div>

      <ProgressBar current={currentIdx + 1} total={questions.length} answered={answered} />

      {flaggedCount > 0 && (
        <div className="flagged-banner">🚩 {flaggedCount} flagged for review</div>
      )}

      {/* Live score bar — exam mode only */}
      {isExam && examLive && (
        <div className={`exam-live-score ${examLive.canPass ? "els-can-pass" : "els-will-fail"}`}>
          <div className="els-counts">
            <span className="els-item els-correct">✓ {examLive.correct} correct</span>
            <span className="els-item els-wrong">✗ {examLive.wrong} wrong</span>
            <button className="els-item els-skipped els-skipped-btn" onClick={() => setShowMap(true)} disabled={examLive.skipped === 0}>
              ⊘ {examLive.skipped} skipped {examLive.skipped > 0 ? "↓" : ""}
            </button>
            {examLive.remaining > 0 && (
              <span className="els-item els-remaining">· {examLive.remaining} remaining</span>
            )}
          </div>
          <div className="els-verdict">
            <span className="els-pct" style={{ color: examLive.pct >= PASS_PCT ? "#16a34a" : "#dc2626" }}>
              {examLive.pct}%
            </span>
            {examLive.correct + examLive.wrong + examLive.skipped === 0 ? (
              <span className="els-hint">Need {examLive.needed}/{questions.length} correct to pass</span>
            ) : examLive.pct >= PASS_PCT ? (
              <span className="els-hint els-pass-hint">✓ Passing!</span>
            ) : !examLive.canPass ? (
              <span className="els-hint els-fail-hint">⚠ Can't reach 80% — go back to skipped Qs</span>
            ) : (
              <span className="els-hint">Need {examLive.toGo} more correct to pass</span>
            )}
          </div>
        </div>
      )}

      {/* Skipped indicator on current question */}
      {isExam && currentSkipped && (
        <div className="skipped-notice">⊘ You skipped this question — answer it or leave it blank</div>
      )}

      <QuestionCard
        question={questions[currentIdx]}
        index={currentIdx}
        total={questions.length}
        selected={answers[currentIdx] ?? -1}
        onSelect={handleSelect}
        showAnswer={showAnswer}
        flagged={!!flagged[currentIdx]}
        onFlag={() => setFlagged(prev => ({ ...prev, [currentIdx]: !prev[currentIdx] }))}
        isExam={isExam}
      />

      <div className="quiz-nav">
        <button className="btn btn-secondary btn-nav" onClick={goPrev} disabled={currentIdx === 0}>← Prev</button>
        <button className="btn btn-outline btn-nav" onClick={() => setShowMap(v => !v)}>
          {showMap ? "Close Map" : "Question Map"}
        </button>
        {showSkipBtn ? (
          <button className="btn btn-skip btn-nav" onClick={handleSkip}>⊘ Skip</button>
        ) : (
          <button
            className={`btn btn-nav ${isLastQ ? "btn-submit" : "btn-secondary"}`}
            onClick={isLastQ ? handleSubmit : goNext}
          >
            {isLastQ ? "Submit ✓" : "Next →"}
          </button>
        )}
      </div>

      {showMap && (
        <div className="question-map">
          <div className="map-legend">
            <span><span className="map-dot answered"   /> Correct</span>
            <span><span className="map-dot wrong-dot"  /> Wrong</span>
            <span><span className="map-dot skipped-dot"/> Skipped</span>
            <span><span className="map-dot current-dot"/> Current</span>
            <span><span className="map-dot unanswered-dot" /> Not reached</span>
          </div>
          <div className="map-grid">
            {questions.map((q, i) => {
              let cls = "map-btn";
              if (i === currentIdx) {
                cls += " map-current";
              } else if (answers[i] !== undefined) {
                cls += answers[i] === q.answer ? " map-answered" : " map-wrong";
              } else if (skipped[i]) {
                cls += " map-skipped";
              }
              if (flagged[i]) cls += " map-flagged";
              return (
                <button key={i} className={cls} onClick={() => goTo(i)}>
                  {i + 1}{flagged[i] ? "🚩" : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="pause-row">
        <button className="btn btn-sm btn-outline" onClick={() => setPaused(p => !p)}>
          {paused ? "▶ Resume" : "⏸ Pause"}
        </button>
        {paused && <span className="paused-label">⏸ Timer paused</span>}
        <span className="answered-count">{answered}/{questions.length} answered</span>
      </div>
    </div>
  );
}
